/**
 * Changes proposed by people who do not own the adventure.
 *
 * Mirrors ContributionDtos.cs and the two enums in Enums.cs. Hand-copied, as every type in this
 * folder is — if the server adds a kind and this is not updated, the queue will render it with a
 * blank label rather than fail, so check both when either changes.
 */

/** Mirrors ContributionKind (AdventureCore.Domain.Enums). */
export enum ContributionKind {
  Photo = 1,
  Description = 2,
  OperatingHours = 3,
  Other = 4,
}

/** Mirrors ContributionStatus (AdventureCore.Domain.Enums). */
export enum ContributionStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
  Superseded = 3,
  Withdrawn = 4,
}

export const CONTRIBUTION_KIND_LABELS: Record<ContributionKind, string> = {
  [ContributionKind.Photo]: 'Photo',
  [ContributionKind.Description]: 'Description',
  [ContributionKind.OperatingHours]: 'Opening hours',
  [ContributionKind.Other]: 'Something else',
}

export const CONTRIBUTION_STATUS_LABELS: Record<ContributionStatus, string> = {
  [ContributionStatus.Pending]: 'Waiting',
  [ContributionStatus.Approved]: 'Accepted',
  [ContributionStatus.Rejected]: 'Not used',
  [ContributionStatus.Superseded]: 'Replaced',
  [ContributionStatus.Withdrawn]: 'Withdrawn',
}

export interface ContributionModerationDto {
  id: string
  adventureId: string
  adventureTitle: string
  kind: ContributionKind
  /** "FullDescription" or "QuickDescription" for a description; empty otherwise. */
  targetKey: string
  /** The proposal, shaped by kind. Parse with payloadFor(). */
  payloadJson?: string | null
  note?: string | null
  status: ContributionStatus
  submittedAt: string
  reviewedAt?: string | null
  decisionReason?: string | null
  /** The proposed photograph, for a photo suggestion. */
  mediaUrl?: string | null

  contributorUserId: string
  contributorDisplayName: string

  /** What accepting would overwrite — the current description, or the current hours JSON. */
  currentValue?: string | null
  currentCoverUrl?: string | null

  /** Whether the photograph arrived carrying coordinates, which the server removed. */
  hadLocationData: boolean

  contributorApprovedCount: number
  contributorRejectedCount: number
}

// The payload shapes, matching the C# records.
export interface PhotoPayload {
  caption?: string | null
  sourceUrl?: string | null
  author?: string | null
  licenseName?: string | null
  licenseUrl?: string | null
}

export interface DescriptionPayload {
  proposed: string
}

export interface OperatingDay {
  day: number
  closed?: boolean
  open?: string | null
  close?: string | null
}

export interface OperatingOverride {
  start: string
  end: string
  closed?: boolean
  open?: string | null
  close?: string | null
  label?: string | null
}

export interface OperatingHours {
  weekly: OperatingDay[]
  overrides: OperatingOverride[]
  notes?: string | null
}

export interface OperatingHoursPayload {
  operatingHours: OperatingHours
  intentionallyEmpty: boolean
}

export interface OtherPayload {
  text: string
}

/**
 * Read a submission's payload, or null if it cannot be read.
 *
 * Returns null rather than throwing: a malformed payload should leave one row in the queue looking
 * empty, not take the whole page down with it.
 */
export function payloadFor<T>(row: ContributionModerationDto): T | null {
  if (!row.payloadJson) return null
  try {
    return JSON.parse(row.payloadJson) as T
  } catch {
    return null
  }
}
